/**
 * PRE-DEPLOY VALIDATION REGISTRY — SINGLE SOURCE OF TRUTH
 * =======================================================
 * The operating rule this file encodes:
 *
 *   "Whenever a change touches payments, credits, memberships, listings,
 *    search, workflows or integrations, the corresponding regression /
 *    preflight / concurrency test is run and reviewed before the change is
 *    considered complete."
 *
 * Every suite below already existed and was already running somewhere in the
 * admin panel (or only on a cron, with no way to run it on demand). What was
 * missing was the MAPPING: given the area a change touched, which suites must
 * be green before shipping. That mapping lives here and nowhere else, so the
 * gate cannot drift away from the suites it is supposed to gate.
 *
 * Nothing in this file performs I/O. Each check declares HOW it is run and how
 * to read its answer; src/lib/releaseChecks.ts does the running and recording.
 *
 * IMPORTANT: every `interpret()` below was written against the real response
 * shape of its function. When a payload is missing or unrecognised the verdict
 * is 'attention' (needs a human), never a silent 'pass'.
 */

export type AreaId =
  | 'payments'
  | 'credits'
  | 'memberships'
  | 'listings'
  | 'search'
  | 'workflows'
  | 'integrations';

export interface ChangeArea {
  id: AreaId;
  en: string;
  es: string;
  /** Concrete examples so the operator can pick correctly without guessing. */
  examplesEn: string;
}

export const CHANGE_AREAS: ChangeArea[] = [
  {
    id: 'payments',
    en: 'Payments',
    es: 'Pagos',
    examplesEn: 'checkout links, payment webhook, refunds, disputes, pricing',
  },
  {
    id: 'credits',
    en: 'Lead credits',
    es: 'Créditos de leads',
    examplesEn: 'unlocking leads, credit ledger, capacity limits, renewals',
  },
  {
    id: 'memberships',
    en: 'Memberships',
    es: 'Membresías',
    examplesEn: 'plans, subscription records, included leads, activation chain',
  },
  {
    id: 'listings',
    en: 'Listings',
    es: 'Anuncios',
    examplesEn: 'posting, moderation, expiry, photos, the public payload',
  },
  {
    id: 'search',
    en: 'Search & browse',
    es: 'Búsqueda y catálogo',
    examplesEn: 'the /jobs feed, filters, pagination, rate limiting',
  },
  {
    id: 'workflows',
    en: 'Role workflows',
    es: 'Flujos por rol',
    examplesEn: 'homeowner / subcontractor / GC journeys, dashboards, permissions',
  },
  {
    id: 'integrations',
    en: 'Integrations',
    es: 'Integraciones',
    examplesEn: 'GoHighLevel CRM, webhooks, secrets, outbound sync',
  },
];

export type VerdictStatus = 'pass' | 'fail' | 'attention';

export interface CheckVerdict {
  status: VerdictStatus;
  /** One line an operator can act on. */
  summary: string;
  /** Supporting lines — failures first. */
  detail: string[];
}

export interface ReleaseCheck {
  id: string;
  en: string;
  es: string;
  /** What shipping without this check would risk. */
  provesEn: string;
  /** 'client' runs in the browser; 'edge' invokes a Supabase function. */
  kind: 'client' | 'edge';
  /** Edge function slug (kind === 'edge'). */
  fn?: string;
  body?: Record<string, unknown>;
  /** Some functions authenticate the caller themselves without a session. */
  requireAuth?: boolean;
  areas: AreaId[];
  /** 'slow' suites create + delete synthetic fixtures and take ~15-60s. */
  duration: 'fast' | 'slow';
  /** True when the suite writes synthetic rows (always cleaned up, no card charged). */
  writesFixtures: boolean;
  /** Where the full, detailed panel for this suite lives. */
  fullPanelEn: string;
  interpret: (data: any) => CheckVerdict;
}

const list = (xs: unknown): string[] => (Array.isArray(xs) ? xs.map((x) => String(x)) : []);

/** Used when a function answered but not in the shape this gate understands. */
const unreadable = (what: string): CheckVerdict => ({
  status: 'attention',
  summary: `${what} ran but returned an unrecognised payload — review it in its own panel before shipping.`,
  detail: [],
});

export const RELEASE_CHECKS: ReleaseCheck[] = [
  // -------------------------------------------------------------------------
  {
    id: 'browse-regression',
    en: 'Browse & search regression',
    es: 'Regresión de búsqueda',
    provesEn:
      'The feed renders what the API returned: no stuck spinner, no false "Zero listings", filters combine, and no protected field (zip, owner id, contact, documents) reaches a locked card.',
    kind: 'client',
    areas: ['listings', 'search'],
    duration: 'fast',
    writesFixtures: false,
    fullPanelEn: 'Security → Browse regression',
    interpret: (data: any) => {
      const cases = Array.isArray(data) ? data : [];
      if (!cases.length) return unreadable('The browse regression suite');
      const failed = cases.filter((c: any) => !c?.passed);
      return {
        status: failed.length ? 'fail' : 'pass',
        summary: `${cases.length - failed.length}/${cases.length} cases passed`,
        detail: failed.length
          ? failed.map((c: any) => `FAILED — ${c.name}: ${c.detail}`)
          : cases.slice(0, 3).map((c: any) => `${c.name}: ${c.detail}`),
      };
    },
  },

  // -------------------------------------------------------------------------
  {
    id: 'launch-preflight',
    en: 'Checkout & CRM preflight',
    es: 'Preflight de checkout y CRM',
    provesEn:
      'Every plan checkout URL is reachable and advertises the correct monthly price (never a term total), the GoHighLevel key is accepted live, and the webhook secrets are configured.',
    kind: 'edge',
    fn: 'launch-preflight',
    areas: ['payments', 'memberships', 'integrations'],
    duration: 'fast',
    writesFixtures: false,
    fullPanelEn: 'Webhook & recovery',
    interpret: (data: any) => {
      if (!data || typeof data.launchReady === 'undefined') return unreadable('The preflight');
      const blocking = list(data.blockingIssues);
      const warnings = list(data.warnings);
      if (blocking.length)
        return {
          status: 'fail',
          summary: `${blocking.length} blocking issue(s) — do not ship`,
          detail: [...blocking.map((b) => `BLOCKING — ${b}`), ...warnings.map((w) => `warning — ${w}`)],
        };
      if (warnings.length)
        return {
          status: 'attention',
          summary: `No blocking issues, ${warnings.length} item(s) need a human eyeball`,
          detail: warnings.map((w) => `warning — ${w}`),
        };
      return {
        status: 'pass',
        summary: 'Checkout URLs, live CRM key and webhook secrets all verified',
        detail: [String(data.caveat || '')].filter(Boolean),
      };
    },
  },

  // -------------------------------------------------------------------------
  {
    id: 'credit-concurrency-test',
    en: 'Credit concurrency proof',
    es: 'Prueba de concurrencia de créditos',
    provesEn:
      'Under genuinely simultaneous requests: credits never go negative, a contractor never unlocks more leads than credits held, a lead never exceeds 6 contractors, and duplicate/replayed requests cannot double-spend.',
    kind: 'edge',
    fn: 'credit-concurrency-test',
    areas: ['credits', 'payments', 'memberships'],
    duration: 'slow',
    writesFixtures: true,
    fullPanelEn: 'Credits & capacity',
    interpret: (data: any) => {
      if (!data || typeof data.passed === 'undefined') return unreadable('The concurrency suite');
      const failedCases = (Array.isArray(data.results) ? data.results : []).filter((c: any) => !c?.passed);
      const failedAudit = (Array.isArray(data.audit) ? data.audit : []).filter((a: any) => !a?.passed);
      return {
        status: data.passed && !failedAudit.length ? 'pass' : 'fail',
        summary: `${data.passed_cases ?? '?'}/${data.total_cases ?? '?'} cases · ${
          failedAudit.length
        } invariant violation(s) across live data`,
        detail: [
          ...failedCases.map((c: any) => `FAILED — ${c.title}: expected ${c.expected}, observed ${c.actual}`),
          ...failedAudit.map((a: any) => `VIOLATION — ${a.check_key}: ${a.violations} row(s) — ${a.detail}`),
        ],
      };
    },
  },

  // -------------------------------------------------------------------------
  {
    id: 'payment-acceptance-test',
    en: 'Payment pipeline acceptance',
    es: 'Aceptación del flujo de pago',
    provesEn:
      'A real signed delivery to the live webhook still produces: payment event → payment record → subscription → credit balance, and an unsigned delivery is still refused. Synthetic buyer, no card charged.',
    kind: 'edge',
    fn: 'payment-acceptance-test',
    areas: ['payments', 'memberships', 'credits', 'integrations'],
    duration: 'slow',
    writesFixtures: true,
    fullPanelEn: 'Payments → Branding & acceptance',
    interpret: (data: any) => {
      const summary = data?.summary;
      if (!summary || typeof summary !== 'object') return unreadable('The acceptance test');
      const entries = Object.entries(summary as Record<string, unknown>);
      const failed = entries.filter(([, v]) => String(v).toUpperCase() === 'FAIL');
      const passed = entries.filter(([, v]) => String(v).toUpperCase() === 'PASS');
      const other = entries.filter(
        ([, v]) => !['PASS', 'FAIL'].includes(String(v).toUpperCase()),
      );
      const rows = (Array.isArray(data.membership) ? data.membership : []).map(
        (m: any) =>
          `${m.plan}: HTTP ${m.httpStatus} · ${m.webhookResult} · credits ${m.creditsBefore} → ${m.creditsAfter}`,
      );
      if (failed.length)
        return {
          status: 'fail',
          summary: `${failed.length} stage(s) failed end to end`,
          detail: [...failed.map(([k]) => `FAILED — ${k}`), ...rows],
        };
      if (!passed.length) return unreadable('The acceptance test');
      return {
        status: other.length ? 'attention' : 'pass',
        summary: `${passed.length} stage(s) passed${other.length ? `, ${other.length} inconclusive` : ''}`,
        detail: [...other.map(([k, v]) => `${k}: ${String(v)}`), ...rows],
      };
    },
  },

  // -------------------------------------------------------------------------
  {
    id: 'ops-monitor',
    en: 'Live operations sweep',
    es: 'Barrido de operaciones',
    provesEn:
      'The twelve-category sweep over the last 24 hours (payments, CRM sync, email, security, capacity) raises no new alert that the change could have caused.',
    kind: 'edge',
    fn: 'ops-monitor',
    areas: ['payments', 'credits', 'memberships', 'listings', 'search', 'workflows', 'integrations'],
    duration: 'fast',
    writesFixtures: false,
    fullPanelEn: 'Security → Live operations',
    // Response shape verified against the `Sweep` interface consumed by
    // AdminOpsMonitor.tsx: { ranAt, healthy, raised, deduped, findings[], errors[] }.
    // `findings[]` is the real field — an absent findings array means the payload
    // was NOT understood, and that is reported as 'attention', never as a pass.
    interpret: (data: any) => {
      if (!data?.ranAt || !Array.isArray(data.findings)) return unreadable('The operations sweep');
      const findings = data.findings as any[];
      const critical = findings.filter((f) => f?.severity === 'critical');
      const lines = findings
        .slice(0, 12)
        .map((f) => `${f.severity || 'info'} — ${f.type || 'finding'}: ${f.detail || f.title || f.message || ''}`);
      const sweepErrors = list(data.errors).map((e) => `sweep warning — ${e}`);

      if (critical.length || data.healthy === false)
        return {
          status: 'fail',
          summary: `${critical.length} critical finding(s) in the last 24h`,
          detail: [...lines, ...sweepErrors],
        };
      if (findings.length || Number(data.raised || 0) > 0 || sweepErrors.length)
        return {
          status: 'attention',
          summary: `${findings.length} finding(s) · ${data.raised ?? 0} alert(s) raised — review before shipping`,
          detail: [...lines, ...sweepErrors],
        };
      return { status: 'pass', summary: 'Every category clean — no findings, no alerts raised', detail: [] };
    },
  },

  // -------------------------------------------------------------------------
  {
    id: 'security-verify',
    en: 'Role & permission verification',
    es: 'Verificación de roles y permisos',
    provesEn:
      'Real homeowner / GC / subcontractor accounts are created and signed in, then proven unable to grant themselves admin, credits or verification badges, or to read admin-only tables. A legitimate admin is proven still able to work.',
    kind: 'edge',
    fn: 'security-verify',
    areas: ['workflows', 'listings', 'credits'],
    duration: 'slow',
    writesFixtures: true,
    fullPanelEn: 'Security',
    interpret: (data: any) => {
      const roles = data?.nonAdminRoles;
      if (!roles || typeof roles !== 'object') return unreadable('The role verification');
      const problems: string[] = [];
      const ok: string[] = [];
      Object.entries(roles as Record<string, any>).forEach(([role, r]) => {
        if (r?.error) problems.push(`${role}: fixture error — ${r.error}`);
        else if (r?.escalation_blocked === true) ok.push(`${role}: escalation blocked, admin tables unreadable`);
        else problems.push(`ESCALATION — ${role} kept a privileged field it set itself`);
      });
      const admin = data?.legitimateAdmin;
      if (admin?.error) problems.push(`admin fixture error — ${admin.error}`);
      else if (admin && admin.allowlist_bootstrap_granted_is_admin !== true)
        problems.push('REGRESSION — an allowlisted administrator was NOT granted admin access');
      Object.entries((admin?.endpoints || {}) as Record<string, any>).forEach(([fn, v]) => {
        if (String(v?.note || '').startsWith('REGRESSION')) problems.push(`REGRESSION — admin refused by ${fn}`);
      });
      return {
        status: problems.length ? 'fail' : 'pass',
        summary: problems.length
          ? `${problems.length} permission problem(s) found`
          : `${ok.length} role(s) verified · fixtures removed`,
        detail: problems.length ? problems : ok,
      };
    },
  },
];

export const checkById = (id: string): ReleaseCheck | undefined => RELEASE_CHECKS.find((c) => c.id === id);

/** The suites required for a set of touched areas. Derived — never hand-listed. */
export const checksForAreas = (areas: AreaId[]): ReleaseCheck[] =>
  areas.length === 0 ? [] : RELEASE_CHECKS.filter((c) => c.areas.some((a) => areas.includes(a)));

export const areaLabel = (id: AreaId, es: boolean): string => {
  const a = CHANGE_AREAS.find((x) => x.id === id);
  return a ? (es ? a.es : a.en) : id;
};
